import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useEffect, useRef } from "react";
import { EramCpdlcSession } from "@poscon/eram-cpdlc";
import { abnormalCpdlcDialogStates, EramCpdlcDialog, formatCpdlcMessage } from "@poscon/eram-cpdlc";
import { useDispatch } from "react-redux";
import { eramTextDimensionMap } from "../../eramFont.js";
import { useRootSelector } from "../stateSelectors.js";
import { sectorIdSelector } from "../slices/connectionSlice.js";
import { EramPrompt } from "./EramPrompt.js";
import { MenuElement } from "./MenuElement.js";
import { createItemOption, ViewItemOptionContainer } from "./ViewItemOptionContainer.js";
import { colorNameMap } from "../../colorNameMap.js";
import { heldTocOptions, msgOutColOptionFactory } from "./CpdlcMsgTable.js";
import { selectedViewOptionSelector, setSelectedViewOption, toggleSelectedViewOption, } from "../slices/commonSlice.js";
import { useCommonContext } from "../contexts/commonContext.js";
import { cpdlcTocHeldView } from "./CpdlcTocHeld.js";
import { CDA_ELIG_SYMBOL } from "@poscon/shared-types";
import { stringToParsedTokenArray, } from "@poscon/shared-types/eram";
export const cpdlcMiniMOView = "CPDLC_MINI_MO";
const fontDim = eramTextDimensionMap[2];
const maxDescriptionLength = 12;
const width = fontDim.width * 18 + 4;
const msgColX = 4;
const statusColX = width - fontDim.width * 5 - 6;
const MiniMORow = ({ fp, dialog, tocMsgs, openHeldTocMenu: openHeldTocMenu, y }) => {
    const dispatch = useDispatch();
    const ref = useRef(null);
    const selectedItem = useRootSelector(selectedViewOptionSelector);
    const { setViewPosition } = useCommonContext();
    const descr = dialog || tocMsgs?.length === 1 ? formatCpdlcMessage(dialog?.messages[0] ?? tocMsgs[0]) : null;
    const description = descr?.description ?? "";
    const descriptionToShow = description.length > maxDescriptionLength
        ? `${description.slice(0, maxDescriptionLength - 1)}*`
        : description;
    const options = dialog
        ? msgOutColOptionFactory(cpdlcMiniMOView, fp, dialog, () => dispatch(setSelectedViewOption(null)))
        : tocMsgs ? heldTocOptions(fp.id, () => dispatch(setSelectedViewOption(null))) : null;
    const status = dialog ? dialog.eramState : tocMsgs?.length === 1 ? "HELD" : "HELD*";
    let tint = colorNameMap.lightGrey;
    if (dialog &&
        (dialog.cancelledAt ||
            (EramCpdlcDialog.dialogIsClosed(dialog) &&
                (!abnormalCpdlcDialogStates.includes(status) || dialog.acknowledgedAt)))) {
        tint = colorNameMap.mediumGrey;
    }
    const statusTint = dialog && abnormalCpdlcDialogStates.includes(status) && !dialog.acknowledgedAt
        ? colorNameMap.orange
        : tint;
    const basePath = `${cpdlcMiniMOView}/row/${fp.id}` + (dialog ? `/dialog/${dialog.id}` : "/toc");
    const itemOptions = selectedItem === `${basePath}/status` && options
        ? options.statusOptions
        : selectedItem === `${basePath}/msg` && descr
            ? [createItemOption(descr.longDescription)]
            : null;
    const onmousedown = (path, offset = 0) => {
        if (tocMsgs?.length && selectedItem !== path && path.endsWith("msg")) {
            if (ref.current) {
                const pos = ref.current.getGlobalPosition();
                setViewPosition(cpdlcTocHeldView, { x: pos.x + offset + 1, y: pos.y });
            }
            openHeldTocMenu(fp.id);
        }
        else {
            dispatch(toggleSelectedViewOption(path));
        }
    };
    return (_jsxs("container", { ref: ref, y: y, eventMode: "static", zIndex: selectedItem?.startsWith(basePath) ? 5 : 4, children: [_jsx(MenuElement, { x: 4, text: descriptionToShow, tint: tint, disabled: descriptionToShow.length === 0, selected: selectedItem === `${basePath}/msg`, borderColor: selectedItem === `${basePath}/msg` ? tint : 0, fillColor: 0, selectReverseVideo: true, onmousedown: () => onmousedown(`${basePath}/msg`, 4 + descriptionToShow.length * fontDim.width + 5) }), _jsx(MenuElement, { x: statusColX, text: status, selected: selectedItem === `${basePath}/status`, tint: statusTint, borderColor: selectedItem === `${basePath}/status` ? tint : 0, fillColor: 0, selectReverseVideo: true, onmousedown: () => onmousedown(`${basePath}/status`, statusColX + status.length * fontDim.width + 5) }), selectedItem?.startsWith(basePath) && itemOptions && itemOptions.length > 0 && (_jsx(ViewItemOptionContainer, { baseX: selectedItem === `${basePath}/msg` ? msgColX : statusColX, xOffset: selectedItem === `${basePath}/msg`
                    ? descriptionToShow.length * fontDim.width + 5
                    : status.length * fontDim.width + 5, options: itemOptions, maxRowWidth: fontDim.width * 30 + 4 }))] }));
};
export function miniMODialogFilter(dialog) {
    return !dialog.deleted && dialog.messages[0].type === "UM";
}
export const CpdlcMiniMO = ({ fp, session, dispatchCommand, openHeldTocMenu, ref, ...promptProps }) => {
    const sectorId = useRootSelector(sectorIdSelector);
    const dialogs = Object.values(session.dialogMap).reduce((acc, dialog) => {
        // only display messages sent by system or this sector
        if ((!dialog.eligiblePosition || dialog.eligiblePosition.sectorId === sectorId) &&
            miniMODialogFilter(dialog)) {
            acc.push(dialog);
        }
        return acc;
    }, []);
    const isLda = !session.logicalDataAuthority || session.logicalDataAuthority === sectorId?.split(".").at(0);
    const isEligible = session.eligibleSectorId === sectorId;
    const eligibleShortSectorId = isLda && session.eligibleSectorId ? session.eligibleSectorId.split(".").at(-1) : null;
    const tocMsgs = isEligible && session.heldTOCs.length > 0 ? session.heldTOCs : null;
    const tocDialog = EramCpdlcSession.getOpenTocDialogs(session)?.at(-1);
    const isTocInProgress = sectorId && tocDialog && tocDialog.messages[0]?.nextSectorId === sectorId;
    let dialogsHeight = dialogs.length * (fontDim.height + 4) + 5;
    const sxHeight = fontDim.height + 1;
    let y = 4;
    if (tocMsgs?.length) {
        dialogsHeight += fontDim.height + 4;
        y += fontDim.height + 4;
    }
    const showView = dialogs.length > 0 || tocMsgs?.length || !isEligible;
    useEffect(() => {
        if (!showView) {
            promptProps.closeMenu();
        }
    }, [showView, promptProps.closeMenu]);
    const isNotOwnedByUser = isLda && !isEligible;
    const height = isNotOwnedByUser ? (!isTocInProgress ? sxHeight : sxHeight * 2) : dialogsHeight;
    return (showView && (_jsxs(EramPrompt, { ref: ref, closeOnClickOutside: true, title: isEligible || !eligibleShortSectorId
            ? fp.callsign
            : `${CDA_ELIG_SYMBOL} ${eligibleShortSectorId} ${fp.callsign}`, width: width, height: height, showX: true, ...promptProps, children: [isNotOwnedByUser && (_jsx(MenuElement, { width: width, eventMode: isTocInProgress ? "none" : "static", text: isTocInProgress
                    ? `${eligibleShortSectorId}-STEAL INVALID\nTOC IN PROGRESS`
                    : "STEAL ELIGIBILITY", tint: isTocInProgress ? colorNameMap.yellow : undefined, fillColor: isTocInProgress ? colorNameMap.black : colorNameMap.cyan, onmousedown: () => {
                    if (!isTocInProgress) {
                        dispatchCommand(stringToParsedTokenArray(`SX ${fp.cid ?? fp.callsign}`));
                    }
                } })), !isNotOwnedByUser && tocMsgs && (_jsx(MiniMORow, { y: 4, fp: fp, openHeldTocMenu: openHeldTocMenu, tocMsgs: tocMsgs }, "toc")), !isNotOwnedByUser &&
                dialogs.map((dialog) => {
                    const _y = y;
                    y += fontDim.height + 4;
                    return (_jsx(MiniMORow, { fp: fp, openHeldTocMenu: openHeldTocMenu, dialog: dialog, y: _y }, dialog.id));
                })] })));
};
//# sourceMappingURL=CpdlcMiniMO.js.map